import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FileImage, 
  ClipboardList, 
  Plus, 
  Calendar, 
  TrendingUp,
  Eye,
  Trash2,
  Sparkles,
  Home,
  Building2
} from 'lucide-react';
import axios from 'axios';
import { API } from '../config';
import { useAuth } from '../context/AuthContext';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, floor-plan, questionnaire

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) {
      fetchAnalyses();
    }
  }, [user]);

  const fetchAnalyses = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/vastu/my-analyses`, {
        withCredentials: true
      });
      setAnalyses(response.data || []);
    } catch (error) {
      console.error('Failed to fetch analyses:', error);
      setAnalyses([]);
    } finally {
      setLoading(false);
    }
  };

  const deleteAnalysis = async (analysisId) => {
    if (!window.confirm('Are you sure you want to delete this report?')) return;
    
    try {
      await axios.delete(`${API}/vastu/analysis/${analysisId}`, {
        withCredentials: true
      });
      setAnalyses(analyses.filter(a => a.id !== analysisId));
    } catch (error) {
      console.error('Failed to delete analysis:', error);
      alert('Failed to delete report. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-100';
    if (score >= 60) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getPropertyIcon = (type) => {
    return type === 'office' ? Building2 : Home;
  };

  const filteredAnalyses = analyses.filter(analysis => {
    if (filter === 'all') return true;
    if (filter === 'floor-plan') return analysis.created_via !== 'questionnaire';
    if (filter === 'questionnaire') return analysis.created_via === 'questionnaire';
    return true;
  });

  if (authLoading) {
    return (
      <div className="main-content min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#232323] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="main-content min-h-screen bg-gradient-to-br from-[var(--accent-saffron)] via-white to-[var(--accent-blue-200)] py-12 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Greeting Header */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-[#232323] mb-2">
                Namaste, {user.name?.split(' ')[0] || 'there'}! 🙏
              </h1>
              <p className="text-[#353535]">
                Welcome to your Vastu dashboard. Start a new analysis or view your past reports.
              </p>
            </div>
            
            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => navigate('/upload')}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-[#232323] text-white rounded-xl hover:bg-[#353535] transition-all font-semibold shadow-md"
              >
                <FileImage className="w-5 h-5" />
                Upload Floor Plan
              </button>
              <button
                onClick={() => navigate('/questionnaire')}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-white text-[#232323] border-2 border-[#232323] rounded-xl hover:bg-[var(--accent-blue-200)] transition-all font-semibold"
              >
                <ClipboardList className="w-5 h-5" />
                Quick Assessment
              </button>
            </div>
          </div>
        </div>

        {/* Reports Section */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Header with Filter */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold text-[#232323]">Your Vastu Reports</h2>
                <p className="text-sm text-[#353535] mt-1">
                  {analyses.length} {analyses.length === 1 ? 'report' : 'reports'} saved
                </p>
              </div>
              
              {/* Filter Tabs */}
              <div className="flex bg-[var(--accent-blue-200)] rounded-lg p-1">
                <button
                  onClick={() => setFilter('all')}
                  className={`px-4 py-2 rounded-md text-sm font-semibold transition-all ${
                    filter === 'all' 
                      ? 'bg-white text-[#232323] shadow-sm' 
                      : 'text-[#353535] hover:text-[#232323]'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setFilter('floor-plan')}
                  className={`px-4 py-2 rounded-md text-sm font-semibold transition-all ${
                    filter === 'floor-plan' 
                      ? 'bg-white text-[#232323] shadow-sm' 
                      : 'text-[#353535] hover:text-[#232323]'
                  }`}
                >
                  Floor Plan
                </button>
                <button
                  onClick={() => setFilter('questionnaire')}
                  className={`px-4 py-2 rounded-md text-sm font-semibold transition-all ${
                    filter === 'questionnaire' 
                      ? 'bg-white text-[#232323] shadow-sm' 
                      : 'text-[#353535] hover:text-[#232323]'
                  }`}
                >
                  Quick Check
                </button>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <div className="w-12 h-12 border-4 border-[#232323] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-[#353535]">Loading your reports...</p>
            </div>
          ) : filteredAnalyses.length === 0 ? (
            /* Empty State */
            <div className="p-12 text-center">
              <div className="w-24 h-24 bg-[var(--accent-saffron)] rounded-full flex items-center justify-center mx-auto mb-6">
                <Sparkles className="w-12 h-12 text-[#232323]" />
              </div>
              <h3 className="text-2xl font-bold text-[#232323] mb-2">
                {filter === 'all' ? 'No Reports Yet' : `No ${filter === 'floor-plan' ? 'Floor Plan' : 'Quick Check'} Reports`}
              </h3>
              <p className="text-[#353535] mb-8 max-w-md mx-auto">
                {filter === 'all' 
                  ? "Start your Vastu journey today! Upload a floor plan for detailed analysis or take our quick assessment."
                  : `You haven't created any ${filter === 'floor-plan' ? 'floor plan' : 'questionnaire-based'} reports yet.`
                }
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => navigate('/upload')}
                  className="flex items-center justify-center gap-2 px-6 py-3 bg-[#232323] text-white rounded-xl hover:bg-[#353535] transition-all font-semibold"
                >
                  <Plus className="w-5 h-5" />
                  Upload Floor Plan
                </button>
                <button
                  onClick={() => navigate('/questionnaire')}
                  className="flex items-center justify-center gap-2 px-6 py-3 border-2 border-[#232323] text-[#232323] rounded-xl hover:bg-[var(--accent-blue-200)] transition-all font-semibold"
                >
                  <ClipboardList className="w-5 h-5" />
                  Take Quick Assessment
                </button>
              </div>
            </div>
          ) : (
            /* Reports Grid */
            <div className="p-6">
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {filteredAnalyses.map((analysis) => {
                  const PropertyIcon = getPropertyIcon(analysis.property_type);
                  const score = analysis.analysis_result?.overall_score || 0;
                  const isQuestionnaire = analysis.created_via === 'questionnaire';
                  
                  return (
                    <div
                      key={analysis.id}
                      className="bg-gradient-to-br from-white to-[var(--accent-blue-200)]/30 border-2 border-gray-200 rounded-xl p-5 hover:border-[#232323] hover:shadow-md transition-all group"
                    >
                      {/* Card Header */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                            isQuestionnaire ? 'bg-[var(--accent-purple-200)]' : 'bg-[var(--accent-saffron)]'
                          }`}>
                            {isQuestionnaire 
                              ? <ClipboardList className="w-6 h-6 text-[#232323]" />
                              : <FileImage className="w-6 h-6 text-[#232323]" />
                            }
                          </div>
                          <div>
                            <h3 className="font-bold text-[#232323] capitalize flex items-center gap-2">
                              <PropertyIcon className="w-4 h-4" />
                              {analysis.property_type || 'Home'}
                            </h3>
                            <p className="text-xs text-[#353535]">
                              {isQuestionnaire ? 'Quick Assessment' : 'Floor Plan Analysis'}
                            </p>
                          </div>
                        </div>
                        
                        {/* Score Badge */}
                        <div className={`px-3 py-1 rounded-full font-bold text-sm ${getScoreColor(score)}`}>
                          {score}%
                        </div>
                      </div>

                      {/* Analysis Direction */}
                      <div className="mb-4">
                        <p className="text-sm text-[#353535]">
                          <span className="font-semibold">Direction:</span>{' '}
                          <span className="capitalize">{analysis.orientation || 'Unknown'}</span>-facing
                        </p>
                      </div>

                      {/* Score Bar */}
                      <div className="mb-4">
                        <div className="flex items-center justify-between text-xs text-[#353535] mb-1">
                          <span>Vastu Score</span>
                          <span className="font-semibold">{score}/100</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all ${
                              score >= 80 ? 'bg-green-500' : score >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${score}%` }}
                          />
                        </div>
                      </div>

                      {/* Date */}
                      <div className="flex items-center gap-2 text-sm text-[#353535] mb-4">
                        <Calendar className="w-4 h-4" />
                        {formatDate(analysis.created_at)}
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2 pt-3 border-t border-gray-200">
                        <button
                          onClick={() => navigate(`/report/${analysis.id}`)}
                          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-[#232323] text-white rounded-lg hover:bg-[#353535] transition-all text-sm font-semibold"
                        >
                          <Eye className="w-4 h-4" />
                          View Report
                        </button>
                        <button
                          onClick={() => deleteAnalysis(analysis.id)}
                          className="px-4 py-2 border-2 border-gray-300 text-[#353535] rounded-lg hover:border-red-500 hover:text-red-500 transition-all"
                          title="Delete report"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Tips Section */}
        <div className="mt-8 bg-[#FAF6EE] border border-[#EBE6DD] rounded-2xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center flex-shrink-0">
              <TrendingUp className="w-6 h-6 text-[#232323]" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-[#232323] mb-2">Improve Your Vastu Score</h3>
              <p className="text-sm text-[#353535]">
                For the best results, upload a clear floor plan image. Our AI can detect room layouts 
                and provide more accurate recommendations when working with detailed floor plans.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
